import { type FC } from 'react'

import { InputLabel } from '../inputLabel'
import { OptionLabel } from '../optionLabel'

import { Checkbox } from './Checkbox'
import type { CheckboxOptionProps } from './types'

/**
 * Checkbox.Option component for checkbox with integrated label.
 *
 * Provides a complete checkbox input with label wrapper using InputLabel and OptionLabel.
 * Automatically handles sizing and styling consistency.
 *
 * @example
 * Simple usage with text:
 * ```tsx
 * <Checkbox.Option
 *   checked={accepted}
 *   onChange={(e) => setAccepted(e.target.checked)}
 * >
 *   Accept terms and conditions
 * </Checkbox.Option>
 * ```
 *
 * @example
 * With complex content:
 * ```tsx
 * <Checkbox.Option
 *   checked={premium}
 *   onChange={(e) => setPremium(e.target.checked)}
 * >
 *   <div>
 *     <div className="font-semibold">Premium Plan</div>
 *     <div className="text-sm">$29.99/month</div>
 *   </div>
 * </Checkbox.Option>
 * ```
 *
 * @example
 * With indeterminate state:
 * ```tsx
 * <Checkbox.Option
 *   checked={allSelected}
 *   indeterminate={someSelected && !allSelected}
 *   onChange={handleSelectAll}
 * >
 *   Select all items
 * </Checkbox.Option>
 * ```
 */
export const CheckboxOption: FC<CheckboxOptionProps> = ({
  children,
  disabled,
  size = 'md',
  ...checkboxProps
}) => {
  return (
    <InputLabel disabled={disabled}>
      <Checkbox {...checkboxProps} disabled={disabled} size={size} />
      <OptionLabel size={size}>{children}</OptionLabel>
    </InputLabel>
  )
}

CheckboxOption.displayName = 'CheckboxOption'
